import { getFormattedTimeWindow } from '@modules/charts/utils';
import { getDateRangeEndTime } from '@modules/common/utils';
import { JOIN_QUERY_ALIAS } from '@modules/quantify/constants';
import { getFieldLabelColumn } from '@modules/quantify/hooks/useExportQuantifyReportDataAsCSV/helpers/getFieldLabelColumn';
import {
  QuantifyQuery,
  QuantifyReportDataPoint,
} from '@modules/quantify/store/types';
import { isAggregateFormula } from '@modules/quantify/utils';
import dayjs from 'dayjs';
import { CsvColumnType } from 'generated/graphql';
import { AggregatePeriod } from 'v2/core/entities';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getTimeSeriesAnalysisColumnsParams {
  groupBy: QuantifyQuery['groupBy'];
  formulaID: EQLFunctionName;
  quantifyChartDataPoints: QuantifyReportDataPoint[] | null;
  aggregatePeriod: AggregatePeriod;
  formulaDisplayName?: string;
  shouldRemoveFileUploadPrefix: boolean;
  isUnifiedFieldEnabled: boolean;
}

export function getTimeSeriesAnalysisColumns({
  groupBy,
  formulaID,
  aggregatePeriod,
  quantifyChartDataPoints,
  formulaDisplayName,
  shouldRemoveFileUploadPrefix,
  isUnifiedFieldEnabled,
}: getTimeSeriesAnalysisColumnsParams) {
  const timestampsSet = new Set<number>();
  quantifyChartDataPoints?.forEach((dataPoint) => {
    if (dataPoint.timestamp && !timestampsSet.has(dataPoint.timestamp)) {
      timestampsSet.add(dataPoint.timestamp);
    }
  });
  const sortedTimestamps = Array.from(timestampsSet).sort((a, b) => a - b);
  const shouldShowFloatingValue = formulaID !== EQLFunctionName.Count;

  const getFormulaColumnTitle = () => {
    if (isAggregateFormula(formulaID)) {
      return formulaDisplayName ?? '';
    }

    return formulaID === EQLFunctionName.NormaliseByPercent
      ? 'Feedback Percentage'
      : 'Feedback Count';
  };

  const formulaValueColumn = {
    title: getFormulaColumnTitle(),
    type: shouldShowFloatingValue ? CsvColumnType.Float : CsvColumnType.Integer,
    queryID: JOIN_QUERY_ALIAS,
  };

  return [
    ...(isAggregateFormula(formulaID)
      ? [formulaValueColumn]
      : [
          getFieldLabelColumn({
            groupBy,
            shouldRemoveFileUploadPrefix,
            isUnifiedFieldEnabled,
          }),
        ]),
    ...sortedTimestamps.map((timestamp) => {
      const startDate = dayjs(timestamp).tz();
      const endDate = getDateRangeEndTime(startDate, aggregatePeriod);
      return {
        title: getFormattedTimeWindow({
          startDate,
          endDate,
          aggregatePeriod,
          firstTimeWindow:
            sortedTimestamps.length > 0
              ? dayjs(sortedTimestamps[0]).tz()
              : undefined,
          lastTimeWindow:
            sortedTimestamps.length > 0
              ? dayjs(sortedTimestamps[sortedTimestamps.length - 1]).tz()
              : undefined,
        }),
        type: shouldShowFloatingValue
          ? CsvColumnType.Float
          : CsvColumnType.Integer,
        timestamp,
      };
    }),
    ...(isAggregateFormula(formulaID) ? [] : [formulaValueColumn]),
  ];
}
